import React, { useMemo, useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { BADGE_TIERS, formatPurityScore, getBadgeImageUrl } from '../constants';
import { getUserStats } from '../utils/userTrackingManager';
import {
  getCurrentBadge,
  getNextBadge,
  getProgressToNextTier,
  getScoreToNextTier,
  getEarnedToiletBadges,
  BADGE_TIER_DATA,
  TOILET_BADGES,
} from '../utils/badgeManager';
import './DashboardGrid.css';

/**
 * DASHBOARD GRID LAYOUT
 *
 * Layout Structure:
 * - Top Row: Purity Card (left) + Stats Card (right)
 * - Middle: Games Bar (full width, Toilet Mode only)
 * - Bottom Row: Cleanup Card (left) + Distillery Card (right)
 */

// ─── Count-Up Hook ─────────────────────────────────────────────────────────
// Animates a number from 0 to `target` over `duration` ms on mount.
// Re-triggers whenever `target` changes (new purification = satisfying roll-up).
function useCountUp(target, duration = 1200) {
  const [display, setDisplay] = useState(0);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const fromRef = useRef(0);

  useEffect(() => {
    // Don't animate dashes / non-numeric states
    if (typeof target !== 'number') {
      setDisplay(target);
      return;
    }

    const from = fromRef.current;
    const delta = target - from;

    // Skip animation for zero delta
    if (delta === 0) return;

    startRef.current = null;

    const animate = (timestamp) => {
      if (!startRef.current) startRef.current = timestamp;
      const elapsed = timestamp - startRef.current;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(from + delta * eased));

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        fromRef.current = target;
      }
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, duration]);

  return display;
}

function DashboardGrid({
  // Purity card props
  purityScore,
  currentTier,
  isWalletConnected,
  isDemoMode,

  // Game buttons props
  isToiletHolder,
  isDemoToiletMode,
  isWheelAvailable,
  setShowSpinWheel,
  setShowFlushFrenzy,
  setShowScratchOff,

  // Quarantine Button
  quarantineButton,

  // Protected Assets Button
  protectedAssetsButton,

  // Children components
  cleanupContent,
  distilleryContent,
}) {
  const userStats = getUserStats();

  // ─── Badge calculations ────────────────────────────────────────────────
  const currentBadge = useMemo(() => getCurrentBadge(purityScore), [purityScore]);
  const nextBadge = useMemo(() => getNextBadge(purityScore), [purityScore]);
  const progress = useMemo(() => getProgressToNextTier(purityScore), [purityScore]);
  const scoreNeeded = useMemo(() => getScoreToNextTier(purityScore), [purityScore]);
  const isMaxTier = !nextBadge;

  // ─── Count-up targets (only animate when wallet connected & not demo) ──
  const isLive = isWalletConnected && !isDemoMode;
  const purityTarget     = isLive ? purityScore                              : null;
  const purifyTarget     = isLive ? (userStats.totalPurifications || 0)      : null;
  const objectsTarget    = isLive ? (userStats.totalObjectsCleaned || 0)     : null;
  const toiletTarget     = isToiletHolder ? (userStats.toiletModePurifications || 0) : null;
  const spinTarget       = isToiletHolder ? (userStats.spinWheelWins || 0)           : null;
  const frenzyTarget     = isToiletHolder ? (userStats.flushFrenzyHighScore || 0)    : null;

  const animPurity    = useCountUp(purityTarget  ?? 0, 1400);
  const animPurify    = useCountUp(purifyTarget  ?? 0, 1200);
  const animObjects   = useCountUp(objectsTarget ?? 0, 1300);
  const animToilet    = useCountUp(toiletTarget  ?? 0, 1200);
  const animSpin      = useCountUp(spinTarget    ?? 0, 1000);
  const animFrenzy    = useCountUp(frenzyTarget  ?? 0, 1000);

  // Toilet Mode badge eligibility check
  const toiletStats = useMemo(() => ({
    isToiletHolder,
    toiletModePurifications: userStats.toiletModePurifications || 0,
    spinWheelWins: userStats.spinWheelWins || 0,
    flushFrenzyHighScore: userStats.flushFrenzyHighScore || 0,
  }), [isToiletHolder, userStats]);

  const earnedToiletBadges = useMemo(() => getEarnedToiletBadges(toiletStats), [toiletStats]);

  // ─── Background toggle (Toilet Mode only) ─────────────────────────────
  const [useNormalBg, setUseNormalBg] = useState(() => {
    return localStorage.getItem('toilet_bg_preference') === 'normal';
  });

  useEffect(() => {
    if (useNormalBg) {
      document.body.classList.add('toilet-bg-normal');
      localStorage.setItem('toilet_bg_preference', 'normal');
    } else {
      document.body.classList.remove('toilet-bg-normal');
      localStorage.setItem('toilet_bg_preference', 'toilet');
    }
    // Cleanup on unmount
    return () => document.body.classList.remove('toilet-bg-normal');
  }, [useNormalBg]);

  return (
    <div className="dashboard-grid-container">

      {/* ── TOP LEFT: Purity Score & Badge ─────────────────────────────── */}
      <motion.div
        className="grid-card purity-card"
        initial={{ x: -50, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        <div className="purity-card-content">

          {/* Badge Emoji (large, color-matched glow) */}
          <motion.div
            className={`purity-badge-icon ${isMaxTier ? 'badge-pulse' : ''}`}
            whileHover={{ scale: 1.1 }}
            style={{ '--badge-glow': currentBadge.glowColor }}
          >
            <div
              className="badge-emoji-large"
              style={{ filter: `drop-shadow(0 0 20px ${currentBadge.glowColor})` }}
            >
              {currentBadge.emoji}
            </div>
          </motion.div>

          {/* Tier Name */}
          <div className="purity-tier-section">
            <div className="tier-label">TIER</div>
            <div className="tier-value" style={{ color: (!isWalletConnected || isDemoMode) ? '#64748b' : currentBadge.color }}>
              {(!isWalletConnected || isDemoMode) ? 'Connect Wallet' : currentBadge.name}
            </div>
            <div className="tier-description">{currentBadge.description}</div>
          </div>

          {/* Progress to Next Tier */}
          <div className="purity-progress-section">
            {isMaxTier ? (
              <>
                <div className="progress-label max-tier-label">✨ MAX TIER ACHIEVED</div>
                <div className="progress-bar-new">
                  <motion.div
                    className="progress-fill-new max-tier-fill"
                    initial={{ width: 0 }}
                    animate={{ width: '100%' }}
                    transition={{ duration: 0.8 }}
                  />
                </div>
                <div className="progress-numbers" style={{ color: currentBadge.color }}>
                  MYTHIC — ABSOLUTE LEGEND 🌟
                </div>
              </>
            ) : (
              <>
                <div className="progress-label">
                  PROGRESS TO {nextBadge.name.toUpperCase()}
                </div>
                <div className="progress-bar-new">
                  <motion.div
                    className="progress-fill-new"
                    style={{
                      background: `linear-gradient(90deg, ${currentBadge.color}, ${nextBadge.color})`,
                    }}
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                  />
                </div>
                <div className="progress-numbers">
                  <span style={{ color: currentBadge.color }}>{progress}%</span>
                  <span className="progress-separator"> · </span>
                  <span style={{ color: 'rgba(226,232,240,0.5)' }}>
                    {scoreNeeded.toLocaleString()} pts to {nextBadge.name}
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Quarantine Button */}
          {quarantineButton && (
            <div className="protected-button-wrapper">
              {quarantineButton}
            </div>
          )}

          {/* Protected Assets Button */}
          {protectedAssetsButton && (
            <div className="protected-button-wrapper">
              {protectedAssetsButton}
            </div>
          )}
        </div>

        {/* 🚽 Toilet Badges (shown only if any earned) */}
        {isToiletHolder && earnedToiletBadges.length > 0 && (
          <div className="toilet-badges-row">
            <div className="toilet-badges-header">
              <div className="toilet-badges-label">TOILET BADGES</div>
            </div>
            <div className="toilet-badges-list">
              {earnedToiletBadges.map(badge => (
                <motion.div
                  key={badge.id}
                  className="toilet-badge-chip"
                  title={`${badge.name}: ${badge.description}`}
                  whileHover={{ scale: 1.1 }}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <span className="toilet-badge-emoji">{badge.emoji}</span>
                  <span className="toilet-badge-name">{badge.name}</span>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </motion.div>

      {/* ── TOP RIGHT: User Stats ───────────────────────────────────────── */}
      <motion.div
        className="grid-card stats-card"
        initial={{ x: 50, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        <div className="stats-card-content">
          <div className="stat-item-new">
            <div className="stat-label-new">PURITY POINTS</div>
            <div className={`stat-value-new ${!isLive ? 'stat-empty' : ''}`}>
              {!isLive ? '--' : (
                <span className="stat-value-counting">
                  {formatPurityScore(animPurity)}
                </span>
              )}
            </div>
          </div>

          <div className="stat-item-new">
            <div className="stat-label-new">PURIFICATIONS</div>
            <div className={`stat-value-new ${!isLive ? 'stat-empty' : ''}`}>
              {!isLive ? '--' : (
                <span className="stat-value-counting">
                  {animPurify.toLocaleString()}
                </span>
              )}
            </div>
          </div>

          <div className="stat-item-new">
            <div className="stat-label-new">OBJECTS CLEANED</div>
            <div className={`stat-value-new ${!isLive ? 'stat-empty' : ''}`}>
              {!isLive ? '--' : (
                <span className="stat-value-counting">
                  {animObjects.toLocaleString()}
                </span>
              )}
            </div>
          </div>

          {/* Toilet Mode extra stats row */}
          {isToiletHolder && (
            <>
              <div className="stat-item-new toilet-stat">
                <div className="stat-label-new">🚽 TOILET PURGES</div>
                <div className="stat-value-new toilet-stat-value">
                  <span className="stat-value-counting">{animToilet.toLocaleString()}</span>
                </div>
              </div>

              <div className="stat-item-new toilet-stat">
                <div className="stat-label-new">🎰 SPIN WINS</div>
                <div className="stat-value-new toilet-stat-value">
                  <span className="stat-value-counting">{animSpin.toLocaleString()}</span>
                </div>
              </div>

              <div className="stat-item-new toilet-stat">
                <div className="stat-label-new">⚡ FRENZY BEST</div>
                <div className="stat-value-new toilet-stat-value">
                  <span className="stat-value-counting">{animFrenzy.toLocaleString()}</span>
                </div>
              </div>
            </>
          )}

          <div className="stats-building">
            {isToiletHolder
              ? '🚽 Toilet Warrior — Filth doesn\'t stand a chance.'
              : '💎 Building your score...'}
          </div>
        </div>
      </motion.div>

      {/* ── MIDDLE: Mini-Game Buttons (Toilet Mode only) ────────────────── */}
      {(isToiletHolder || isDemoToiletMode) && (
        <motion.div
          className="grid-card games-bar"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <div className="games-bar-content">
            {/* Label */}
            <div className="games-bar-label">MINI GAMES:</div>

            {/* Buttons */}
            <div className="games-bar-buttons">
              <motion.button
                className="game-button enabled"
                onClick={() => setShowScratchOff(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="game-name">Scratch Off</span>
              </motion.button>

              <motion.button
                className={`game-button ${isWheelAvailable ? 'enabled' : 'disabled'}`}
                disabled={!isWheelAvailable}
                onClick={() => isWheelAvailable && setShowSpinWheel(true)}
                whileHover={isWheelAvailable ? { scale: 1.05 } : {}}
                whileTap={isWheelAvailable ? { scale: 0.95 } : {}}
              >
                <span className="game-name">Spin Wheel</span>
              </motion.button>

              <motion.button
                className="game-button enabled"
                onClick={() => setShowFlushFrenzy(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="game-name">Flush Frenzy</span>
              </motion.button>
            </div>
          </div>
        </motion.div>
      )}

      {/* ── BOTTOM LEFT: Wallet Cleanup ─────────────────────────────────── */}
      <motion.div
        className="grid-card cleanup-card"
        initial={{ x: -50, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        <div className="cleanup-panel">
          {cleanupContent}
        </div>
      </motion.div>

      {/* ── BOTTOM RIGHT: Distillery ─────────────────────────────────────── */}
      <motion.div
        className="grid-card distillery-card"
        initial={{ x: 50, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        {distilleryContent}
      </motion.div>
    </div>
  );
}

export default DashboardGrid;
